// New Relic Browser Agent Loader
/**
 * New Relic loading state constants
 */
const NEW_RELIC_LOADING_STATE = {
    IDLE: 'idle',
    LOADING: 'loading',
    LOADED: 'loaded',
    FAILED: 'failed'
};
// Current loading state
let loadingState = NEW_RELIC_LOADING_STATE.IDLE;
/**
 * Loads the New Relic browser agent
 * @returns Promise that resolves when loading is complete or fails
 */
export async function loadNewRelic() {
    // Make sure APP_SETTINGS exists
    if (!window.APP_SETTINGS?.ENV) {
        console.error('APP_SETTINGS.ENV is not defined, cannot load New Relic');
        return Promise.resolve();
    }
    // Skip loading New Relic for development/staging environments
    if (!/test|production/i.test(window.APP_SETTINGS.ENV)) {
        console.log(`New Relic loading skipped in ${window.APP_SETTINGS.ENV} environment.`);
        return Promise.resolve();
    }
    // Prevent multiple loads
    if (loadingState !== NEW_RELIC_LOADING_STATE.IDLE) {
        console.log(`New Relic already in state: ${loadingState}`);
        return Promise.resolve();
    }
    // Set to LOADING state
    loadingState = NEW_RELIC_LOADING_STATE.LOADING;
    return new Promise(resolve => {
        // Backup Zone's original Promise
        const originalPromise = window.Promise;
        try {
            // Set up error handler in case script fails to load
            const timeout = setTimeout(() => {
                loadingState = NEW_RELIC_LOADING_STATE.FAILED;
                console.warn('New Relic took too long to load, continuing without it');
                resolve();
            }, 5000); // 5 second timeout
            const script = document.createElement('script');
            script.src = `/assets/sso-imlp-gateway-new-relic-agent-${window.APP_SETTINGS.ENV}.js`;
            script.type = 'application/javascript';
            script.async = true;
            script.onload = () => {
                // Restore the original Promise to avoid conflict with Zone.js
                window.Promise = originalPromise;
                clearTimeout(timeout);
                loadingState = NEW_RELIC_LOADING_STATE.LOADED;
                console.log('New Relic loaded successfully.');
                // Add page load timing marker
                if (window.newrelic) {
                    window.newrelic.setCustomAttribute('appLoadTimestamp', Date.now());
                }
                resolve();
            };
            script.onerror = (err) => {
                console.error('Failed to load New Relic.', err);
                clearTimeout(timeout);
                window.Promise = originalPromise;
                loadingState = NEW_RELIC_LOADING_STATE.FAILED;
                // Resolve instead of reject to prevent app from crashing
                resolve();
            };
            document.head.appendChild(script);
        }
        catch (err) {
            console.error('Error setting up New Relic', err);
            window.Promise = originalPromise;
            loadingState = NEW_RELIC_LOADING_STATE.FAILED;
            resolve();
        }
    });
}
